import {
    PersistedUserEvent,
    type PersistedUserEventSmsConsentMetadata,
} from '@fruitful/types';
import { capitalize } from 'lodash';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { Platform } from 'react-native';
import {
    BodyMediumRegular,
    Icon,
    isWeb,
    Spinner,
    Switch,
    TimerIcon,
    useToastController,
    View,
    XStack,
} from 'smoothie';

import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import useGetUserEvents from '../../events/hooks/useGetUserEvents';
import { useLogUserEvent } from '../../events/hooks/useLogUserEvent';
import { captureException } from '../../logger/Logger';
import { SmsConsentSheet } from '../../notifications/components/SmsConsentSheet';
import {
    hasGrantedPushNotificationsPermission,
    isNotificationEnabled,
    userHasSmsConsentForCurrentPhoneNumber,
} from '../../notifications/helpers';
import useRegisterForPushNotifications from '../../notifications/hooks/useRegisterForPushNotifications';
import {
    SMS_OPT_IN_DISCLOSURE_HEADLINE,
    SMS_OPT_IN_DISCLOSURE_TEXT,
} from '../../notifications/smsConsentDisclosure';
import { useUser } from '../../user/providers/UserProvider';
import useUpdateNotificationChannel from '../hooks/useUpdateNotificationChannel';
import {
    NotificationChannel,
    type NotificationType,
} from '../types/UserNotificationChannel';

interface NotificationsLineItemProps {
    title: string;
    type: NotificationType;
    channel: NotificationChannel;
}

const NotificationsLineItem = ({
    title,
    type,
    channel,
}: NotificationsLineItemProps) => {
    const user = useAuthedUser();
    const { refetch } = useUser();
    const toast = useToastController();
    const [isEnabled, setIsEnabled] = useState<boolean | null>(null);

    const { mutateAsync: updateNotificationChannel } =
        useUpdateNotificationChannel();
    const { registerForPushNotifications } = useRegisterForPushNotifications();
    const { logUserEvent } = useLogUserEvent();
    const [isSmsConsentSheetOpen, setIsSmsConsentSheetOpen] = useState(false);
    const {
        data: smsConsentUserEvents,
        isLoading: isSmsConsentUserEventsLoading,
        refetch: refetchSmsConsentUserEvents,
    } = useGetUserEvents(
        { eventType: PersistedUserEvent.SMS_CONSENT_OPT_IN },
        { enabled: channel === NotificationChannel.SMS },
    );

    // Async set the state of the notification toggle.
    useEffect(() => {
        const setNotificationValue = async () => {
            const notificationValue = await isNotificationEnabled(
                user,
                type,
                channel,
                smsConsentUserEvents,
            );
            setIsEnabled(notificationValue);
        };

        void setNotificationValue();
    }, [isSmsConsentUserEventsLoading]);

    const updateNotificationValue = async (enabled: boolean) => {
        setIsEnabled(enabled);

        try {
            await updateNotificationChannel({
                body: {
                    type,
                    channel,
                    enabled,
                },
            });

            // Refresh user with new notification settings.
            void refetch?.();

            toast.show('Notifications updated', {
                duration: 3000,
                description: `You will ${
                    !enabled ? 'not' : ''
                } receive ${channel.toLowerCase()} notifications for ${title.toLowerCase()}`,
                icon: (
                    <View
                        backgroundColor="$gray100"
                        padding={8}
                        borderRadius={4}
                    >
                        <Icon icon={TimerIcon} />
                    </View>
                ),
            });
        } catch (e) {
            captureException(e);
            toast.show(`Error updating ${title.toLowerCase()} notifications`, {
                duration: 2000,
            });
            setIsEnabled(!enabled); // Revert flag if it didn't work
        }
    };

    const onConfirmSmsConsent = async () => {
        setIsSmsConsentSheetOpen(false);
        const metadata: PersistedUserEventSmsConsentMetadata = {
            phoneNumber: user.phoneNumber ?? '',
            disclosureText: `${SMS_OPT_IN_DISCLOSURE_HEADLINE} ${SMS_OPT_IN_DISCLOSURE_TEXT}`,
            platform: Platform.OS,
        };
        logUserEvent(PersistedUserEvent.SMS_CONSENT_OPT_IN, metadata);
        await updateNotificationValue(true);
        void refetchSmsConsentUserEvents();
    };

    const onUpdate = async (enabled: boolean) => {
        // For push notifications, handle requesting permissions and registering device, if needed.
        if (channel === NotificationChannel.PUSH) {
            if (!(await hasGrantedPushNotificationsPermission())) {
                try {
                    await registerForPushNotifications();
                } catch (e) {
                    toast.show(
                        'Device is not registered for push notifications. Enable notifications in your phone settings.',
                        {
                            duration: 2000,
                        },
                    );
                    return;
                }
            }
        } else if (channel === NotificationChannel.SMS && enabled) {
            if (
                !userHasSmsConsentForCurrentPhoneNumber(
                    smsConsentUserEvents,
                    user,
                )
            ) {
                setIsSmsConsentSheetOpen(true);
                return; // wait for the callback from the consent sheet to actually persist the change
            }
        }

        await updateNotificationValue(enabled);
    };

    const getChannelLabel = (channel: NotificationChannel) => {
        if (channel === NotificationChannel.SMS) {
            return 'SMS';
        }
        return capitalize(channel);
    };

    return (
        <>
            <XStack
                alignItems="center"
                justifyContent="space-between"
                paddingBottom={20}
                marginBottom={20}
                borderBottomWidth={1}
                borderBottomColor="$gray100"
            >
                <BodyMediumRegular paddingLeft={12}>
                    {getChannelLabel(channel)}{' '}
                    {isWeb && channel === NotificationChannel.PUSH && '(iOS)'}
                </BodyMediumRegular>
                {/* If isEnabled is null, the value has not been set async yet. */}
                {isEnabled === null ||
                (channel === NotificationChannel.SMS &&
                    isSmsConsentUserEventsLoading) ? (
                    <Spinner />
                ) : (
                    <Switch isChecked={isEnabled} onCheckedChange={onUpdate} />
                )}
            </XStack>
            {channel === NotificationChannel.SMS && (
                <SmsConsentSheet
                    isOpen={isSmsConsentSheetOpen}
                    onClose={() => {
                        setIsSmsConsentSheetOpen(false);
                    }}
                    onConfirm={() => {
                        void onConfirmSmsConsent();
                    }}
                />
            )}
        </>
    );
};

export default NotificationsLineItem;
